import { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Car, Clipboard, User, ChevronRight, ChevronLeft, Upload } from 'lucide-react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Textarea } from './ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Progress } from './ui/progress';
import { Link } from 'react-router-dom';
import { trackLeadConversion } from '../utils/googleAds';

const WHATSAPP_NUMBER = "491798067007";

// WhatsApp SVG Logo
const WhatsAppLogo = ({ className }) => (
  <svg className={className} viewBox="0 0 24 24" fill="currentColor">
    <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/>
  </svg>
);

const carBrands = [
  'Audi', 'BMW', 'Mercedes-Benz', 'Volkswagen', 'Opel', 'Ford', 'Toyota', 
  'Honda', 'Mazda', 'Renault', 'Peugeot', 'Citroën', 'Skoda', 'Seat', 
  'Fiat', 'Hyundai', 'Kia', 'Nissan', 'Volvo', 'Porsche', 'Andere'
];

const fuelTypes = ['Benzin', 'Diesel', 'Elektro', 'Hybrid', 'Plug-in-Hybrid', 'Gas (LPG/CNG)'];
const transmissionTypes = ['Automatik', 'Schaltgetriebe'];
const conditionTypes = ['Sehr gut', 'Gut', 'Befriedigend', 'Mit Mängeln', 'Unfallfahrzeug'];
const currentYear = new Date().getFullYear();
const years = Array.from({ length: 35 }, (_, i) => currentYear - i);

export const CarForm = () => {
  const [step, setStep] = useState(1);
  const [formData, setFormData] = useState({
    // Step 1: Vehicle Data
    brand: '',
    model: '',
    year: '',
    mileage: '',
    fuel: '',
    transmission: '',
    power: '',
    // Step 2: Condition
    condition: '',
    tuv: '',
    accident: '',
    wishPrice: '',
    message: '',
    // Step 3: Contact
    name: '',
    phone: '',
    email: '',
  });

  const updateFormData = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const nextStep = () => {
    if (step < 3) setStep(step + 1);
  };

  const prevStep = () => {
    if (step > 1) setStep(step - 1);
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    
    // Construct WhatsApp message
    const message = `*Neue Fahrzeuganfrage*

*Fahrzeugdaten:*
Marke: ${formData.brand}
Modell: ${formData.model}
Baujahr: ${formData.year}
Kilometerstand: ${formData.mileage} km
Kraftstoff: ${formData.fuel}
Getriebe: ${formData.transmission}
Leistung: ${formData.power} PS

*Zustand:*
Zustand: ${formData.condition}
TÜV: ${formData.tuv}
Unfallschaden: ${formData.accident}
${formData.wishPrice ? `Wunschpreis: ${formData.wishPrice} €` : ''}
${formData.message ? `Nachricht: ${formData.message}` : ''}

*Kontaktdaten:*
Name: ${formData.name}
Telefon: ${formData.phone}
E-Mail: ${formData.email}`;

    const encodedMessage = encodeURIComponent(message);
    const whatsappUrl = `https://wa.me/${WHATSAPP_NUMBER}?text=${encodedMessage}`;

    // Google Ads Conversion (nur 1x pro Session, nur wenn Marketing-Consent vorhanden)
    trackLeadConversion();

    window.open(whatsappUrl, '_blank');
  };

  const progressPercentage = (step / 3) * 100;

  const stepInfo = [
    { icon: Car, label: 'Fahrzeugdaten' },
    { icon: Clipboard, label: 'Zustand' },
    { icon: User, label: 'Kontaktdaten' },
  ];

  return (
    <section id="autoformular" data-testid="car-form-section" className="py-20 md:py-28 bg-tiktok-surface">
      <div className="container mx-auto px-4 md:px-6">
        {/* Section Header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="text-center mb-12"
        >
          <span className="text-sm font-semibold uppercase tracking-[0.2em] text-tiktok-pink mb-4 block">
            Kostenlose Bewertung
          </span>
          <h2 className="font-heading text-3xl md:text-4xl lg:text-5xl font-bold text-white mb-4">
            Fahrzeug jetzt bewerten lassen
          </h2>
          <p className="text-gray-400 max-w-2xl mx-auto">
            Füllen Sie das Formular aus und erhalten Sie schnell ein unverbindliches Angebot.
          </p>
        </motion.div>

        {/* Form Container */}
        <div className="max-w-3xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="bg-tiktok-black rounded-2xl border border-white/10 overflow-hidden"
          >
            {/* Progress Bar */}
            <div className="p-6 border-b border-white/10">
              <Progress value={progressPercentage} className="h-2 mb-4" />
              
              {/* Step Indicators */}
              <div className="flex justify-between">
                {stepInfo.map((info, index) => (
                  <button
                    key={index}
                    onClick={() => setStep(index + 1)}
                    data-testid={`form-step-${index + 1}`}
                    className={`flex items-center gap-2 px-3 py-2 rounded-lg transition-colors ${
                      step === index + 1
                        ? 'bg-gradient-to-r from-tiktok-cyan/20 to-tiktok-pink/20 text-white'
                        : step > index + 1
                        ? 'text-tiktok-cyan'
                        : 'text-gray-500'
                    }`}
                  >
                    <info.icon className="w-4 h-4" />
                    <span className="text-sm font-medium hidden sm:inline">{info.label}</span>
                    <span className="text-sm font-medium sm:hidden">{index + 1}</span>
                  </button>
                ))}
              </div>
            </div>

            {/* Form Content */}
            <form onSubmit={handleSubmit} className="p-6">
              <AnimatePresence mode="wait">
                {/* Step 1: Vehicle Data */}
                {step === 1 && (
                  <motion.div
                    key="step1"
                    initial={{ opacity: 0, x: 20 }}
                    animate={{ opacity: 1, x: 0 }}
                    exit={{ opacity: 0, x: -20 }}
                    className="space-y-4"
                  >
                    <div className="grid sm:grid-cols-2 gap-4">
                      <div>
                        <Label className="text-gray-300 mb-2 block">Marke *</Label>
                        <Select value={formData.brand} onValueChange={(v) => updateFormData('brand', v)}>
                          <SelectTrigger data-testid="input-brand" className="bg-tiktok-surface border-white/10 text-white">
                            <SelectValue placeholder="Marke wählen" />
                          </SelectTrigger>
                          <SelectContent className="bg-tiktok-elevated border-white/10">
                            {carBrands.map((brand) => (
                              <SelectItem key={brand} value={brand} className="text-white hover:bg-white/10">
                                {brand}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                      <div>
                        <Label className="text-gray-300 mb-2 block">Modell *</Label>
                        <Input
                          data-testid="input-model"
                          value={formData.model}
                          onChange={(e) => updateFormData('model', e.target.value)}
                          placeholder="z.B. A4 Avant"
                          className="bg-tiktok-surface border-white/10 text-white"
                        />
                      </div>
                    </div>

                    <div className="grid sm:grid-cols-2 gap-4">
                      <div>
                        <Label className="text-gray-300 mb-2 block">Baujahr *</Label>
                        <Select value={formData.year} onValueChange={(v) => updateFormData('year', v)}>
                          <SelectTrigger data-testid="input-year" className="bg-tiktok-surface border-white/10 text-white">
                            <SelectValue placeholder="Baujahr wählen" />
                          </SelectTrigger>
                          <SelectContent className="bg-tiktok-elevated border-white/10 max-h-60">
                            {years.map((year) => (
                              <SelectItem key={year} value={year.toString()} className="text-white hover:bg-white/10">
                                {year}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                      <div>
                        <Label className="text-gray-300 mb-2 block">Kilometerstand *</Label>
                        <Input
                          data-testid="input-mileage"
                          type="number"
                          value={formData.mileage}
                          onChange={(e) => updateFormData('mileage', e.target.value)}
                          placeholder="z.B. 85000"
                          className="bg-tiktok-surface border-white/10 text-white"
                        />
                      </div>
                    </div>

                    <div className="grid sm:grid-cols-3 gap-4">
                      <div>
                        <Label className="text-gray-300 mb-2 block">Kraftstoff *</Label>
                        <Select value={formData.fuel} onValueChange={(v) => updateFormData('fuel', v)}>
                          <SelectTrigger data-testid="input-fuel" className="bg-tiktok-surface border-white/10 text-white">
                            <SelectValue placeholder="Wählen" />
                          </SelectTrigger>
                          <SelectContent className="bg-tiktok-elevated border-white/10">
                            {fuelTypes.map((fuel) => (
                              <SelectItem key={fuel} value={fuel} className="text-white hover:bg-white/10">
                                {fuel}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                      <div>
                        <Label className="text-gray-300 mb-2 block">Getriebe *</Label>
                        <Select value={formData.transmission} onValueChange={(v) => updateFormData('transmission', v)}>
                          <SelectTrigger data-testid="input-transmission" className="bg-tiktok-surface border-white/10 text-white">
                            <SelectValue placeholder="Wählen" />
                          </SelectTrigger>
                          <SelectContent className="bg-tiktok-elevated border-white/10">
                            {transmissionTypes.map((trans) => (
                              <SelectItem key={trans} value={trans} className="text-white hover:bg-white/10">
                                {trans}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                      <div>
                        <Label className="text-gray-300 mb-2 block">Leistung (PS)</Label>
                        <Input
                          data-testid="input-power"
                          type="number"
                          value={formData.power}
                          onChange={(e) => updateFormData('power', e.target.value)}
                          placeholder="z.B. 150"
                          className="bg-tiktok-surface border-white/10 text-white"
                        />
                      </div>
                    </div>
                  </motion.div>
                )}

                {/* Step 2: Condition */}
                {step === 2 && (
                  <motion.div
                    key="step2"
                    initial={{ opacity: 0, x: 20 }}
                    animate={{ opacity: 1, x: 0 }}
                    exit={{ opacity: 0, x: -20 }}
                    className="space-y-4"
                  >
                    <div className="grid sm:grid-cols-2 gap-4">
                      <div>
                        <Label className="text-gray-300 mb-2 block">Zustand *</Label>
                        <Select value={formData.condition} onValueChange={(v) => updateFormData('condition', v)}>
                          <SelectTrigger data-testid="input-condition" className="bg-tiktok-surface border-white/10 text-white">
                            <SelectValue placeholder="Zustand wählen" />
                          </SelectTrigger>
                          <SelectContent className="bg-tiktok-elevated border-white/10">
                            {conditionTypes.map((cond) => (
                              <SelectItem key={cond} value={cond} className="text-white hover:bg-white/10">
                                {cond}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                      <div>
                        <Label className="text-gray-300 mb-2 block">TÜV *</Label>
                        <Select value={formData.tuv} onValueChange={(v) => updateFormData('tuv', v)}>
                          <SelectTrigger data-testid="input-tuv" className="bg-tiktok-surface border-white/10 text-white">
                            <SelectValue placeholder="TÜV Status" />
                          </SelectTrigger>
                          <SelectContent className="bg-tiktok-elevated border-white/10">
                            <SelectItem value="mehr-als-6-monate" className="text-white hover:bg-white/10">Mehr als 6 Monate</SelectItem>
                            <SelectItem value="weniger-als-6-monate" className="text-white hover:bg-white/10">Weniger als 6 Monate</SelectItem>
                            <SelectItem value="abgelaufen" className="text-white hover:bg-white/10">Abgelaufen</SelectItem>
                            <SelectItem value="kein-tuv" className="text-white hover:bg-white/10">Kein TÜV</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                    </div>

                    <div className="grid sm:grid-cols-2 gap-4">
                      <div>
                        <Label className="text-gray-300 mb-2 block">Unfallschaden *</Label>
                        <Select value={formData.accident} onValueChange={(v) => updateFormData('accident', v)}>
                          <SelectTrigger data-testid="input-accident" className="bg-tiktok-surface border-white/10 text-white">
                            <SelectValue placeholder="Unfallschaden?" />
                          </SelectTrigger>
                          <SelectContent className="bg-tiktok-elevated border-white/10">
                            <SelectItem value="nein" className="text-white hover:bg-white/10">Nein, unfallfrei</SelectItem>
                            <SelectItem value="ja-repariert" className="text-white hover:bg-white/10">Ja, repariert</SelectItem>
                            <SelectItem value="ja-nicht-repariert" className="text-white hover:bg-white/10">Ja, nicht repariert</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                      <div>
                        <Label className="text-gray-300 mb-2 block">Wunschpreis (optional)</Label>
                        <Input
                          data-testid="input-wishprice"
                          type="number"
                          value={formData.wishPrice}
                          onChange={(e) => updateFormData('wishPrice', e.target.value)}
                          placeholder="z.B. 15000"
                          className="bg-tiktok-surface border-white/10 text-white"
                        />
                      </div>
                    </div>

                    <div>
                      <Label className="text-gray-300 mb-2 block">Nachricht (optional)</Label>
                      <Textarea
                        data-testid="input-message"
                        value={formData.message}
                        onChange={(e) => updateFormData('message', e.target.value)}
                        placeholder="Weitere Informationen zu Ihrem Fahrzeug..."
                        className="bg-tiktok-surface border-white/10 text-white min-h-[100px]"
                      />
                    </div>
                  </motion.div>
                )}

                {/* Step 3: Contact */}
                {step === 3 && (
                  <motion.div
                    key="step3"
                    initial={{ opacity: 0, x: 20 }}
                    animate={{ opacity: 1, x: 0 }}
                    exit={{ opacity: 0, x: -20 }}
                    className="space-y-4"
                  >
                    <div>
                      <Label className="text-gray-300 mb-2 block">Name *</Label>
                      <Input
                        data-testid="input-name"
                        value={formData.name}
                        onChange={(e) => updateFormData('name', e.target.value)}
                        placeholder="Ihr vollständiger Name"
                        className="bg-tiktok-surface border-white/10 text-white"
                      />
                    </div>

                    <div>
                      <Label className="text-gray-300 mb-2 block">Telefonnummer *</Label>
                      <Input
                        data-testid="input-phone"
                        type="tel"
                        value={formData.phone}
                        onChange={(e) => updateFormData('phone', e.target.value)}
                        placeholder="Ihre Telefonnummer"
                        className="bg-tiktok-surface border-white/10 text-white"
                      />
                    </div>

                    <div>
                      <Label className="text-gray-300 mb-2 block">E-Mail *</Label>
                      <Input
                        data-testid="input-email"
                        type="email"
                        value={formData.email}
                        onChange={(e) => updateFormData('email', e.target.value)}
                        placeholder="Ihre E-Mail-Adresse"
                        className="bg-tiktok-surface border-white/10 text-white"
                      />
                    </div>

                    <div className="p-4 bg-tiktok-surface rounded-xl border border-white/10">
                      <p className="text-sm text-gray-400">
                        Mit dem Absenden stimmen Sie der Verarbeitung Ihrer Daten gemäß unserer{' '}
                        <Link to="/datenschutz" className="text-tiktok-cyan hover:underline">Datenschutzerklärung</Link>{' '}
                        zu. Ihre Anfrage ist kostenlos und unverbindlich.
                      </p>
                    </div>
                  </motion.div>
                )}
              </AnimatePresence>

              {/* Navigation Buttons */}
              <div className="flex justify-between mt-8 pt-6 border-t border-white/10">
                {step > 1 ? (
                  <Button
                    type="button"
                    onClick={prevStep}
                    data-testid="form-prev-btn"
                    variant="outline"
                    className="border-white/20 text-white bg-transparent hover:bg-white/10"
                  >
                    <ChevronLeft className="w-4 h-4 mr-2" />
                    Zurück
                  </Button>
                ) : (
                  <div />
                )}

                {step < 3 ? (
                  <Button
                    type="button"
                    onClick={nextStep}
                    data-testid="form-next-btn"
                    className="btn-gradient text-white font-semibold"
                  >
                    Weiter
                    <ChevronRight className="w-4 h-4 ml-2" />
                  </Button>
                ) : (
                  <Button
                    type="submit"
                    data-testid="form-submit-btn"
                    className="bg-[#25D366] hover:bg-[#20BD5A] text-white font-semibold"
                  >
                    <WhatsAppLogo className="w-4 h-4 mr-2" />
                    Per WhatsApp senden
                  </Button>
                )}
              </div>
            </form>
          </motion.div>
        </div>
      </div>
    </section>
  );
};

export default CarForm;
